import { compact, find, map } from 'lodash';
import type { FC } from 'react';
import { useContext } from 'react';
import React from 'react';

import AddressBlock from '../../../components/review/AddressBlock';
import '../../../components/review/Review.scss';
import ReviewFooter from '../../../components/review/ReviewFooter';
import ReviewItem from '../../../components/review/ReviewItem';
import ReviewSection from '../../../components/review/ReviewSection';
import ConfigVosContext from '../../../service/context/config-vos';
import useAppStoreVos from '../../../service/store/appVos';
import useOrderStoreVos from '../../../service/store/orderVos';
import { scrollToRef } from '../../../utils/scroll';

interface IStepReviewProps {
  onSubmit: () => Promise<void>;
}

const buildItemList = (
  counts: { [key: string]: number } | undefined,
  definitions: { key: string; name: string }[] | undefined
): { key: string; label: string }[] =>
  compact(map(counts, (count, key) => {
    if (!count || count < 1) {
      return undefined;
    }

    const definition = find(definitions, { key });

    if (!definition) {
      return undefined;
    }

    return { key, label: `${definition.name} × ${count}` };
  }));

const StepReview: FC<IStepReviewProps> = ({ onSubmit }) => {
  const config = useContext(ConfigVosContext);
  const setStep = useAppStoreVos(state => state.setStep);
  const order = useOrderStoreVos(state => state.order);

  const {
    appointment,
    dealer,
    manufacturer,
    accessories,
    jaws,
    customer_number,
    reference_number,
    coupon,
    address,
    diffInvoiceRecipient,
    invoiceRecipient
  } = order || {};

  const referenceLabel = config?.settings?.reference_number_label || 'Referenznummer';

  const goToStep = (step: number) => (): void => {
    setStep(step);
    scrollToRef();
  };

  const handleSubmit = (): void => {
    void onSubmit();
    scrollToRef();
  };

  // Hersteller/Maschinen aus {key: anzahl} + Config-Namen auflösen.
  const manufacturerList = compact(map(manufacturer, (count, key) => {
    if (!count || count < 1) {
      return undefined;
    }

    const name = config?.manufacturers_ext?.[key]?.name || key;

    return { key, label: count > 1 ? `${name} × ${count}` : name };
  }));

  const equipmentList = [
    ...buildItemList(accessories, config?.accessories),
    ...buildItemList(jaws, config?.jaws)
  ];

  const location = appointment || dealer;
  const locationName = appointment?.customer || dealer?.customer;
  const locationAddress = [
    location?.street,
    (location as { street_number?: string } | undefined)?.street_number
  ].filter(Boolean).join(' ');
  const locationCity = [ location?.zip, location?.city ].filter(Boolean).join(' ');

  return (
    <div className="review">
      <ReviewSection
        icon="building"
        title="Termin & Fachhandel"
        onEdit={goToStep(1)}
      >
        <ReviewItem label="Termin" value={appointment?.date} />
        <ReviewItem label="Fachhandel" value={locationName} />
        <ReviewItem
          label="Adresse"
          full
          value={location ? (
            <>
              {locationAddress && <span className="review-address-line">{locationAddress}</span>}
              {locationCity && <span className="review-address-line">{locationCity}</span>}
            </>
          ) : undefined}
        />
      </ReviewSection>

      {manufacturerList.length > 0 && (
        <ReviewSection
          icon="wrench"
          title="Maschine"
          onEdit={goToStep(2)}
        >
          <ReviewItem
            label="Hersteller"
            full
            value={(
              <div className="review-products">
                {manufacturerList.map(item => (
                  <div className="review-product" key={item.key}>
                    <span className="review-product-name">{item.label}</span>
                  </div>
                ))}
              </div>
            )}
          />
        </ReviewSection>
      )}

      {equipmentList.length > 0 && (
        <ReviewSection
          icon="package"
          title="Zubehör & Pressbacken"
          onEdit={goToStep(3)}
        >
          <ReviewItem
            label="Ausgewähltes Zubehör"
            full
            value={(
              <div className="review-products">
                {equipmentList.map(item => (
                  <div className="review-product" key={item.key}>
                    <span className="review-product-name">{item.label}</span>
                  </div>
                ))}
              </div>
            )}
          />
        </ReviewSection>
      )}

      <ReviewSection
        icon="users"
        title="Kundendaten"
        onEdit={goToStep(4)}
      >
        <ReviewItem label="Kundennummer" value={customer_number} />
        <ReviewItem label={referenceLabel} value={reference_number} />
        <ReviewItem label="E-Mail" value={address?.email} />
        <ReviewItem label="Telefon" value={address?.phone} />
        <ReviewItem label="Mobilnummer" value={address?.mobile} />
        <ReviewItem label="Gutscheincode" value={coupon} />
        <ReviewItem
          label="Anschrift"
          full
          value={<AddressBlock address={address} />}
        />
      </ReviewSection>

      {diffInvoiceRecipient && invoiceRecipient && (
        <ReviewSection
          icon="file"
          title="Abweichender Rechnungsempfänger"
          onEdit={goToStep(4)}
        >
          <ReviewItem
            label="E-Mail Rechnungsempfänger"
            value={invoiceRecipient.email}
          />
          <ReviewItem
            label="Rechnungsempfänger"
            full
            value={<AddressBlock address={invoiceRecipient} />}
          />
        </ReviewSection>
      )}

      <ReviewFooter
        onSubmit={handleSubmit}
        submitText="Auftrag verbindlich erteilen"
      />
    </div>
  );
};

export default StepReview;
